import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {RouterStateSnapshot} from '@angular/router';
import {interval, Observable, Subscription} from 'rxjs';
import {environment} from 'src/environments/environment';
import {ApplicationPackage} from '../enums/application-package.enum';
import {AlertService} from '../main/alert.service';
import {CustomHttpUrlEncodingCodec} from '../main/encoder';
import {ResponseObj} from '../main/responseObj';
import {PageActionKey, RolePagePermission} from '../model/a2srv-operation/auth-models/page-permission-model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly loginBaseUrl = environment.endPoint + ApplicationPackage.AUTH_OP;

  private isLoggedIn: boolean;
  private userRole: string = '';
  intervalSubs: Subscription;

  constructor(private http: HttpClient, private alertService: AlertService) {
    this.isLoggedIn = false;
  }

  getToken(): string {
    return localStorage.getItem('token');
  }

  loginUser(logininput, captcha: string, forceLogin?: boolean): Observable<ResponseObj> {
    let queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});

    if (forceLogin !== undefined && forceLogin !== null) {
      queryParameters = queryParameters.set('accept', <any>forceLogin);
    }
    if (captcha) {
      queryParameters = queryParameters.set('captchaResponse', <any>captcha);
    }
    return this.http.post(this.loginBaseUrl + `login`, logininput, {params: queryParameters});
  }

  setUserLoggedIn() {
    this.isLoggedIn = true;
  }

  getUserLoggedIn() {
    return this.isLoggedIn;
  }

  setUserRole(role) {
    this.userRole = role;
  }

  getUserRole(): string {
    return this.userRole;
  }

  checkUserRole(role) {
    if (this.userRole == role) {
      return true;
    } else {
      return false;
    }
  }

  signOut(): Observable<ResponseObj> {
    return this.http.post(this.loginBaseUrl + 'logout', null);
  }

  clearSession(expectedToken?: string): boolean {
    if (expectedToken !== undefined && this.getToken() !== expectedToken) {
      return false;
    }

    this.keepAliveExit();
    this.isLoggedIn = false;
    this.userRole = '';
    localStorage.clear();
    return true;
  }

  keepAlive(): void {
    this.keepAliveExit();
    this.intervalSubs = interval(60000).subscribe((t) => {
      const keepAliveSubscription = this.intervalSubs;
      this.http.get(this.loginBaseUrl + 'keepalive').subscribe(
        (response) => {},
        (error) => {
          if (this.isSessionFailure(error) && this.intervalSubs === keepAliveSubscription) {
            this.keepAliveExit();
          }
        },
      );
    });
  }

  keepAliveExit() {
    if (this.intervalSubs) {
      this.intervalSubs.unsubscribe();
      this.intervalSubs = null;
    }
  }

  private isSessionFailure(error: any): boolean {
    return error && (error.status === 400 || error.status === 401 || error.status === 440);
  }

  getSiteKey(): Observable<ResponseObj> {
    return this.http.get(this.loginBaseUrl + 'operational/sitekey');
  }

  userHasAccess(state: RouterStateSnapshot, pageKey?: string) {
    // Admins reach everything.
    if (localStorage.getItem('isAdmin') === 'true') {
      return true;
    }

    if (pageKey) {
      if (this.hasPageAction(pageKey, 'canRead')) {
        return true;
      }
      this.alertService.warning('Your access is denied for this service.');
      return false;
    }

    // Legacy access model: role privileges mapped to a set of allowed UI urls.
    var urls: string[] = JSON.parse(localStorage.getItem('urls')) || [];
    for (let url of urls) {
      if (state.url.includes(url)) {
        return true;
      }
    }

    // New access model: the role's per-page CRUD permissions are enforced by the
    // gateway on every API call, so a role governed by it may navigate the UI.
    if (this.hasPagePermissions()) {
      return true;
    }

    this.alertService.warning('Your access is denied for this service.');
    return false;
  }

  /** Resolves explicit CRUD permission for a page; missing permission fails closed. */
  hasPageAction(pageKey: string, action: PageActionKey): boolean {
    if (localStorage.getItem('isAdmin') === 'true') {
      return true;
    }

    const pagePermissions = this.getPagePermissions();
    const permission = pagePermissions.find((candidate) => candidate && candidate.pageKey === pageKey);
    return !!(permission && permission[action]);
  }

  /** True when the signed-in role is governed by the per-page permission model. */
  private hasPagePermissions(): boolean {
    return this.getPagePermissions().length > 0;
  }

  private getPagePermissions(): RolePagePermission[] {
    try {
      const pagePermissions = JSON.parse(localStorage.getItem('pagePermissions'));
      return Array.isArray(pagePermissions) ? pagePermissions : [];
    } catch (e) {
      return [];
    }
  }

}
